/**
 * Account-scoped history sync (Wave 2.1, §4.11). When a person is signed in, this
 * pushes the derived scan history kept in this browser up to their account and
 * pulls back the merged set, so their progress is the same on every device.
 *
 * It stays optional and best-effort, like the rest of the API layer: signed out,
 * or with no VITE_API_URL, everything keeps working purely on-device. It never
 * throws (a sync failure just leaves the local history untouched).
 *
 * Derived numbers only leave the browser (score, percentile, tier, per-facet
 * percentiles); never a photo, never landmarks. The server rejects anything else.
 */
import { loadHistory, writeHistory, type HistoryEntry } from './history';
import { getToken } from './auth';

const API = (import.meta.env.VITE_API_URL as string | undefined)?.replace(/\/$/, '');

interface ServerEntry {
  entry_id: string;
  captured_at: string;
  overall: number;
  percentile: number;
  tier: string;
  sub: Record<string, number>;
}

function toServer(h: HistoryEntry): ServerEntry {
  return {
    entry_id: h.id,
    captured_at: h.date,
    overall: h.overall,
    percentile: h.percentile,
    tier: h.tier,
    sub: (h.sub ?? {}) as Record<string, number>,
  };
}

function toLocal(s: ServerEntry): HistoryEntry {
  return {
    id: s.entry_id,
    date: s.captured_at,
    overall: s.overall,
    percentile: s.percentile,
    tier: s.tier,
    sub: (s.sub ?? {}) as HistoryEntry['sub'],
  };
}

/**
 * Push this device's local history to the account and pull the merged set back,
 * writing it to localStorage. Returns the merged history, or null when signed out
 * or the backend is unreachable (in which case local history is left as-is).
 */
export async function syncAccountHistory(): Promise<HistoryEntry[] | null> {
  const token = getToken();
  if (!API || !token) return null;
  const local = loadHistory();
  try {
    const res = await fetch(`${API}/v1/account/history`, {
      method: 'POST',
      headers: { 'content-type': 'application/json', authorization: `Bearer ${token}` },
      body: JSON.stringify({ entries: local.map(toServer) }),
    });
    if (!res.ok) return null;
    const data = (await res.json()) as { entries?: ServerEntry[] };
    if (!Array.isArray(data.entries)) return null;
    // The server returns the full merged set (this device's pushed entries plus
    // anything synced from other devices), so it is the source of truth.
    return writeHistory(data.entries.map(toLocal));
  } catch {
    return null;
  }
}
